import { FetchPawBalanceKey, useEmoBalance } from "hooks/paw/useEmoBalance";
import { FetchUsageHistoryKey } from "hooks/paw/useUsageHistory";
import { modalActions } from "modules/modal";
import { streamingActions } from "modules/streaming";
import React, { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSWRConfig } from "swr";
import { convertMapToValues } from "utility";
import { isForbiddenWord } from "utility/streaming";

import type { GiftItemData } from "modules/streaming";
import type { Store } from "store";

export const useSendComment = ({
  mainVideoId,
}: {
  mainVideoId: string | undefined;
}) => {
  const POST_INTERVAL_SECONDS = 5;

  const inputRef = React.useRef<HTMLInputElement>(null);

  const videoAngleId = useSelector(
    (state: Store) => state.streaming.currentVideoId
  );

  const dispatch = useDispatch();
  const { mutate } = useSWRConfig();
  const { paidValue: emoBalance } = useEmoBalance();

  const user = useSelector((state: Store) => state.auth.user);
  const iconUrls = useSelector((state: Store) => state.streaming.iconUrls);
  const eventInfo = useSelector((state: Store) => state.event.displayEvent);
  const giftItemMap = useSelector(
    (state: Store) => state.streaming.giftItemMap
  );
  const ngWords = useSelector(
    (state: Store) => state.streaming.streamingSettings.ngWords
  );
  const localTimeDiff = useSelector((state: Store) => state.auth.localTimeDiff);

  const [comment, setComment] = React.useState<string>("");
  const [postedTime, setPostedTime] = React.useState<number>(0);
  const [disableButton, setDisableButton] = React.useState<boolean>(false);
  const [isOpenGiftDrawer, setIsOpenGiftDrawer] =
    React.useState<boolean>(false);
  const [selectedGiftItem, setSelectedGiftItem] =
    React.useState<GiftItemData | null>(null);

  const giftItems = useMemo(() => {
    return convertMapToValues(giftItemMap)
      .filter((el) => el.display)
      .sort((a, b) => a.priority - b.priority);
  }, [giftItemMap]);

  const canSendComment = !!iconUrls && !disableButton;

  const canPurchaseGiftItem =
    emoBalance - (selectedGiftItem ? selectedGiftItem.values.paid : 0) >= 0;

  const mutatePAW = useCallback(async () => {
    await Promise.all([
      mutate(FetchPawBalanceKey),
      mutate(FetchUsageHistoryKey),
    ]);
  }, [mutate]);

  const handleSetComment = useCallback((comment: string) => {
    const trimComment = comment.trim();
    setComment(trimComment);
  }, []);

  const handleOpenGiftDrawer = useCallback(() => {
    setIsOpenGiftDrawer(true);
  }, []);

  const handleCloseGiftDrawer = useCallback(() => {
    setSelectedGiftItem(null);
    setIsOpenGiftDrawer(false);
  }, []);

  const handleClickSelectedGift = (selectedGift: GiftItemData) => {
    setSelectedGiftItem(selectedGift);
  };

  const handleOpenPawChargeModal = useCallback(() => {
    dispatch(modalActions.togglePawChargeModal({}));
  }, [dispatch]);

  const handleSendComment = useCallback(() => {
    // ユーザーがログインしていない場合は何もしない
    if (!user) return;

    // コメントと一緒にリクエストするユーザーアイコンがない場合は何もしない
    if (!iconUrls) return;

    // コメントが無くてギフトも選択されていない場合は何もしない
    if (!comment && !selectedGiftItem) return;

    if (mainVideoId === undefined) {
      return;
    }

    // ギフトアイテムが購入出来ない場合はチャージ画面を表示する
    if (!canPurchaseGiftItem) {
      handleOpenPawChargeModal();
      return;
    }

    // 過去にコメントを投稿した時間からPOST_INTERVAL_SECONDS秒以内に再度コメントを投稿しようとした場合は何もしない
    const currentDate = new Date(new Date().getTime() + localTimeDiff);
    const postingTime =
      Math.floor(currentDate.getTime() / 1000) + POST_INTERVAL_SECONDS;
    if (postingTime < postedTime + 5) return;

    const isBan = isForbiddenWord(comment, ngWords);
    const isSelectedGiftItem = !!selectedGiftItem;
    dispatch(
      streamingActions.postComment.started({
        eventId: eventInfo.eventId,
        videoId: mainVideoId,
        postData: {
          msg: comment,
          icon: iconUrls["32"],
          uid: user.uid,
          name: user.displayName ?? "",
          color: selectedGiftItem?.colorCode ?? "#000",
          banReason: isBan ? "ngWord" : null,
          isBan,
          isSuperChat: isSelectedGiftItem,
          amount: selectedGiftItem?.values.paid ?? 0,
          giftItemId: selectedGiftItem?._id ?? "",
          videoAngleId,
        },
        isGift: isSelectedGiftItem,
        itemId: selectedGiftItem?.itemId ?? "",
        mutatePAW,
      })
    );

    // コメントを送信後、ボタンを無効化する
    setDisableButton(true);
    // コメントを送信した時間を記録する
    setPostedTime(postingTime);

    // コメントを送信した後、POST_INTERVAL_SECONDS秒後にボタンを再度有効化する
    window.setTimeout(() => {
      setDisableButton(false);
    }, POST_INTERVAL_SECONDS * 1000);

    // コメント送信後、選択したギフトをクリアしてドロワーを閉じる
    setSelectedGiftItem(null);
    handleCloseGiftDrawer();

    // 入力しているコメントをクリアする
    setComment("");
    // コメントの入力フィールドをクリアする
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  }, [
    canPurchaseGiftItem,
    comment,
    selectedGiftItem,
    user,
    iconUrls,
    ngWords,
    localTimeDiff,
    postedTime,
    eventInfo.eventId,
    mainVideoId,
    videoAngleId,
    handleCloseGiftDrawer,
    inputRef,
    dispatch,
    handleOpenPawChargeModal,
    mutatePAW,
  ]);

  return {
    commentInputRef: inputRef,
    iconUrls,
    comment,
    giftItems,
    selectedGiftItem,
    canSendComment,
    canPurchaseGiftItem,
    isOpenGiftDrawer,
    handleSetComment,
    handleSendComment,
    handleOpenGiftDrawer,
    handleCloseGiftDrawer,
    handleClickSelectedGift,
    handleOpenPawChargeModal,
  };
};
